import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { COLORS } from '../config'

// Every mech is built from boxes and cylinders, but the parts inside a single
// joint never move relative to each other. So each joint collects its shapes
// into a `PartBuilder` and bakes them down to a single mesh. That buys the
// detail budget for panel lines and greebles — roughly 70 primitives per mech —
// while emitting fewer draw calls than the previous flat box stack.
//
// The four structural tints (armour, highlight, frame, panel line) ride in a
// vertex colour attribute against one shared material rather than four
// materials, because splitting by material would have meant ~75 meshes per
// mech instead of ~30. Uniform metalness across the structural set is the
// price; panel lines read as recessed because they are near black, which does
// not depend on roughness.
//
// Every structural primitive MUST carry a colour attribute. A geometry without
// one multiplies to black under `vertexColors: true` — the exact regression
// that blackened the dust instances earlier in this project — so `PartBuilder`
// writes the attribute itself and `build` refuses to emit a batch missing it.

export type MechChassis = 'biped' | 'reverse' | 'heavy'

/** A knee-and-ankle chain. `hip` is the thigh pivot. */
export interface MechLegRig {
  hip: THREE.Group
  knee: THREE.Group
  ankle: THREE.Group
  /** Resting joint angles, so animation can lean against the chassis pose. */
  rest: { hip: number; knee: number; ankle: number }
}

/** A shoulder-and-elbow chain. `shoulder` is the upper-arm pivot. */
export interface MechArmRig {
  shoulder: THREE.Group
  elbow: THREE.Group
  rest: { shoulder: number; elbow: number }
}

export interface MechRig {
  root: THREE.Group
  body: THREE.Group
  /** Pelvis. Carries both legs and bobs with the gait. */
  hip: THREE.Group
  /** Everything above the waist. Leans and twists without moving the feet. */
  torso: THREE.Group
  head: THREE.Group
  /** Thigh pivots. Same objects as `legs[n].hip`, kept for compatibility. */
  leftLeg: THREE.Group
  rightLeg: THREE.Group
  /** Shoulder pivots. Same objects as `arms[n].shoulder`. */
  leftArm: THREE.Group
  rightArm: THREE.Group
  legs: [MechLegRig, MechLegRig]
  arms: [MechArmRig, MechArmRig]
  weaponMuzzle: THREE.Object3D
  missilePorts: THREE.Object3D[]
  thrusters: THREE.Mesh[]
  sensor: THREE.Mesh
  shieldRing: THREE.Mesh
  chassis: MechChassis
  /**
   * Hip height that plants the feet on the deck, derived from the assembled
   * leg. Animation bobs and crouches around this rather than around a literal.
   */
  restHipHeight: number
}

interface MechOptions {
  faction: 'player' | 'enemy'
  heavy?: boolean
}

/** Structural tints, written into vertex colours rather than materials. */
type Tint = 'armor' | 'armorLight' | 'frame' | 'panel'

interface MechMaterials {
  /** One vertex-coloured material for every structural primitive. */
  structural: THREE.MeshStandardMaterial
  glow: THREE.Material
  hot: THREE.Material
  tints: Record<Tint, THREE.Color>
}

type Vec3 = [number, number, number]

function applyTransform(
  geometry: THREE.BufferGeometry,
  position: Vec3,
  rotation: Vec3,
): THREE.BufferGeometry {
  const matrix = new THREE.Matrix4().compose(
    new THREE.Vector3(...position),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation)),
    new THREE.Vector3(1, 1, 1),
  )
  geometry.applyMatrix4(matrix)
  return geometry
}

function paint(geometry: THREE.BufferGeometry, colour: THREE.Color): THREE.BufferGeometry {
  const vertices = geometry.attributes.position!.count
  const colours = new Float32Array(vertices * 3)
  for (let index = 0; index < vertices; index += 1) {
    colours[index * 3] = colour.r
    colours[index * 3 + 1] = colour.g
    colours[index * 3 + 2] = colour.b
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3))
  return geometry
}

/**
 * Accumulates shapes for one rigid joint, then bakes them into a single
 * structural mesh (plus one emissive mesh when the joint has lit strips).
 * Geometries are transformed into the joint's local space at `add` time, so
 * callers keep thinking in plain positions and rotations.
 */
class PartBuilder {
  private readonly structural: THREE.BufferGeometry[] = []
  private readonly emissive: THREE.BufferGeometry[] = []

  constructor(private readonly materials: MechMaterials) {}

  add(
    geometry: THREE.BufferGeometry,
    tint: Tint,
    position: Vec3,
    rotation: Vec3 = [0, 0, 0],
  ): this {
    this.structural.push(paint(applyTransform(geometry, position, rotation), this.materials.tints[tint]))
    return this
  }

  /** An emissive strip. Kept apart because it needs the emissive material. */
  glow(geometry: THREE.BufferGeometry, position: Vec3, rotation: Vec3 = [0, 0, 0]): this {
    this.emissive.push(applyTransform(geometry, position, rotation))
    return this
  }

  /**
   * A thin dark strip laid just proud of an armour face. Panel lines are what
   * give an untextured box a sense of scale, and merging makes them free.
   */
  panelLine(size: [number, number, number], position: Vec3): this {
    return this.add(new THREE.BoxGeometry(...size), 'panel', position)
  }

  build(parent: THREE.Object3D): void {
    this.emit(parent, this.structural, this.materials.structural, true)
    this.emit(parent, this.emissive, this.materials.glow, false)
    this.structural.length = 0
    this.emissive.length = 0
  }

  private emit(
    parent: THREE.Object3D,
    geometries: THREE.BufferGeometry[],
    material: THREE.Material,
    requiresColour: boolean,
  ): void {
    if (geometries.length === 0) return
    const merged = geometries.length === 1 ? geometries[0]! : mergeGeometries(geometries, false)
    if (!merged) {
      throw new Error('Mech part geometries could not be merged: mismatched attributes')
    }
    if (requiresColour && !merged.getAttribute('color')) {
      throw new Error('Structural mech geometry is missing its vertex colour attribute')
    }
    const result = new THREE.Mesh(merged, material)
    result.castShadow = true
    result.receiveShadow = true
    parent.add(result)
  }
}

/** Lowest point of the hip subtree in its parent's space, at the rest pose. */
function legFloor(hip: THREE.Group): number {
  hip.updateMatrixWorld(true)
  return new THREE.Box3().setFromObject(hip).min.y
}

function group(parent: THREE.Object3D, position: Vec3, rotationX = 0): THREE.Group {
  const result = new THREE.Group()
  result.position.set(...position)
  result.rotation.x = rotationX
  parent.add(result)
  return result
}

/** sRGB hex to the linear space three.js multiplies vertex colours in. */
function linear(hex: number): THREE.Color {
  return new THREE.Color(hex).convertSRGBToLinear()
}

function buildMaterials(faction: 'player' | 'enemy'): MechMaterials {
  const accent = faction === 'player' ? COLORS.ice : COLORS.threat
  const player = faction === 'player'
  return {
    structural: new THREE.MeshStandardMaterial({
      // White base: the vertex colour is the entire albedo.
      color: 0xffffff,
      vertexColors: true,
      // Painted armour, not bare steel. High metalness plus the arena's dark
      // albedos was most of why the mech read as a black cut-out.
      metalness: 0.28,
      roughness: 0.58,
    }),
    tints: {
      armor: linear(player ? 0x4a5b63 : 0x5e4038),
      armorLight: linear(player ? 0x8b9895 : 0x8d6d63),
      frame: linear(COLORS.metal),
      // Panel lines and shadow gaps: near black, so they read as recessed
      // seams against either armour tone.
      panel: linear(COLORS.metalDark),
    },
    glow: new THREE.MeshStandardMaterial({
      color: 0x050b0d,
      emissive: accent,
      // Kept well under the bloom threshold: these strips should read as lit
      // edges that describe the armour, not as blobs that erase it.
      emissiveIntensity: 1.05,
      toneMapped: true,
    }),
    hot: new THREE.MeshBasicMaterial({
      color: faction === 'player' ? COLORS.iceBright : COLORS.hot,
      transparent: true,
      opacity: 0.82,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  }
}

export function createMech({ faction, heavy = false }: MechOptions): MechRig {
  const chassis: MechChassis = heavy ? 'heavy' : faction === 'player' ? 'biped' : 'reverse'
  const materials = buildMaterials(faction)
  const accent = faction === 'player' ? COLORS.ice : COLORS.threat

  const root = new THREE.Group()
  const body = new THREE.Group()
  root.add(body)

  // The three chassis read differently in silhouette before colour registers:
  // the player stands tall and square, the skirmisher crouches on digitigrade
  // legs, the bulwark is wide and low with slab shoulders.
  const width = chassis === 'heavy' ? 1.24 : chassis === 'reverse' ? 0.88 : 1
  const stance = chassis === 'heavy' ? 1.32 : chassis === 'reverse' ? 0.92 : 1.06

  const hip = group(body, [0, 0, 0])
  buildHip(hip, materials, width)

  const legs: [MechLegRig, MechLegRig] = [
    buildLeg(hip, materials, -stance, chassis, width),
    buildLeg(hip, materials, stance, chassis, width),
  ]

  // Stand the mech on the deck by measuring the assembled leg rather than by
  // carrying a hand-tuned hip height per chassis. Retune a segment length and
  // the feet stay planted; hard-coding the height is how the previous rig
  // ended up 1.4 units underground.
  hip.position.y = -legFloor(hip)

  const torso = group(body, [0, hip.position.y, 0])
  const head = buildTorso(torso, materials, chassis, width)
  const arms: [MechArmRig, MechArmRig] = [
    buildArm(torso, materials, -1, chassis, width),
    buildArm(torso, materials, 1, chassis, width),
  ]

  const weaponMuzzle = buildWeapon(arms[1].elbow, materials, chassis)
  const missilePorts = buildMissilePods(torso, materials, width)
  const thrusters = buildThrusters(torso, legs, materials, width)
  const sensor = buildSensor(head, materials, accent, faction)

  const shieldRing = new THREE.Mesh(
    new THREE.TorusGeometry(3.7, 0.055, 6, 48),
    new THREE.MeshBasicMaterial({
      color: accent,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  )
  shieldRing.position.set(0, 4.3, 0)
  shieldRing.rotation.x = Math.PI / 2
  root.add(shieldRing)

  return {
    root,
    body,
    hip,
    torso,
    head,
    leftLeg: legs[0].hip,
    rightLeg: legs[1].hip,
    leftArm: arms[0].shoulder,
    rightArm: arms[1].shoulder,
    legs,
    arms,
    weaponMuzzle,
    missilePorts,
    thrusters,
    sensor,
    shieldRing,
    chassis,
    restHipHeight: hip.position.y,
  }
}

/** Pelvis block and the skirt plates that hide the hip joints. */
function buildHip(hip: THREE.Group, m: MechMaterials, width: number): void {
  const part = new PartBuilder(m)
  part.add(new THREE.BoxGeometry(2.3 * width, 0.95, 1.6), 'frame', [0, 0, 0])
  part.add(new THREE.BoxGeometry(2.55 * width, 0.42, 1.75), 'armor', [0, 0.38, 0])
  part.panelLine([2.6 * width, 0.09, 1.8], [0, 0.14, 0])
  // Front and rear skirt armour, angled outward.
  part.add(new THREE.BoxGeometry(1.05 * width, 1.35, 0.4), 'armor', [-0.72 * width, -0.5, -0.78], [0.22, 0, -0.12])
  part.add(new THREE.BoxGeometry(1.05 * width, 1.35, 0.4), 'armor', [0.72 * width, -0.5, -0.78], [0.22, 0, 0.12])
  part.add(new THREE.BoxGeometry(1.9 * width, 1.1, 0.38), 'armor', [0, -0.48, 0.82], [-0.18, 0, 0])
  part.panelLine([1.95 * width, 0.08, 0.42], [0, -0.16, 0.84])
  part.build(hip)
}

/** Core, chest plates, backpack and head. Returns the head group. */
function buildTorso(
  torso: THREE.Group,
  m: MechMaterials,
  chassis: MechChassis,
  width: number,
): THREE.Group {
  const part = new PartBuilder(m)
  const chest = chassis === 'heavy' ? 1.18 : 1

  // Waist column, deliberately narrower than both hip and chest so the
  // silhouette pinches at the middle instead of reading as one slab.
  part.add(new THREE.BoxGeometry(1.42 * width, 1.0, 1.32), 'frame', [0, 0.5, 0])
  part.panelLine([1.5 * width, 0.78, 0.1], [0, 0.5, -0.68])

  // Core block, then the lighter upper plate that catches the fill light.
  part.add(new THREE.BoxGeometry(3.05 * width * chest, 1.5, 2.0), 'armor', [0, 1.72, 0])
  part.add(new THREE.BoxGeometry(2.62 * width * chest, 0.78, 2.24), 'armorLight', [0, 2.6, -0.08])
  part.panelLine([3.1 * width * chest, 0.1, 2.05], [0, 2.34, 0])
  part.panelLine([0.12, 1.4, 2.05], [0, 1.72, 0])

  // Chest intake slats, cut into the front face.
  for (let index = 0; index < 3; index += 1) {
    part.panelLine([1.05 * width, 0.13, 0.12], [0, 1.4 + index * 0.28, -1.02])
  }
  // Collar bar bridging the shoulders.
  part.add(new THREE.BoxGeometry(3.4 * width * chest, 0.34, 1.05), 'frame', [0, 3.06, 0.05])

  // Backpack with radiator fins — reads as mass behind the shoulders and
  // anchors the thruster nozzles.
  part.add(new THREE.BoxGeometry(2.2 * width, 1.6, 1.0), 'armor', [0, 2.15, 1.28])
  for (let index = 0; index < 4; index += 1) {
    part.panelLine([2.24 * width, 0.1, 1.04], [0, 1.66 + index * 0.36, 1.3])
  }
  part.add(new THREE.BoxGeometry(0.55, 1.1, 0.55), 'frame', [-1.12 * width, 2.24, 1.5], [0, 0, 0.15])
  part.add(new THREE.BoxGeometry(0.55, 1.1, 0.55), 'frame', [1.12 * width, 2.24, 1.5], [0, 0, -0.15])

  // Emissive strips: narrow, on the chest faces, describing the edge.
  part.glow(new THREE.BoxGeometry(0.1, 0.56, 0.08), [-0.86 * width, 2.6, -1.16], [0, 0, -0.24])
  part.glow(new THREE.BoxGeometry(0.1, 0.56, 0.08), [0.86 * width, 2.6, -1.16], [0, 0, 0.24])
  part.build(torso)

  const head = group(torso, [0, 3.24, -0.16])
  const skull = new PartBuilder(m)
  skull.add(new THREE.BoxGeometry(0.94, 0.56, 0.92), 'frame', [0, 0, 0])
  skull.add(new THREE.BoxGeometry(1.08, 0.3, 0.48), 'armor', [0, 0.24, 0.1])
  skull.panelLine([0.9, 0.07, 0.08], [0, -0.14, -0.47])
  // Antenna, offset so the head is not bilaterally symmetric.
  skull.add(new THREE.BoxGeometry(0.08, 0.52, 0.08), 'frame', [0.42, 0.4, 0.2], [0.3, 0, 0.2])
  // Visor: a narrow band, not a lamp. The old head lit the whole chest.
  skull.glow(new THREE.BoxGeometry(0.86, 0.13, 0.06), [0, 0.02, -0.48])
  skull.build(head)
  return head
}

function buildLeg(
  hip: THREE.Group,
  m: MechMaterials,
  side: number,
  chassis: MechChassis,
  width: number,
): MechLegRig {
  // Reverse-joint legs fold backwards at the knee like a bird's; the resting
  // angles are what make the Z-shape read even when the mech stands still.
  const rest =
    chassis === 'reverse'
      ? { hip: 0.5, knee: -1.02, ankle: 0.54 }
      : chassis === 'heavy'
        ? { hip: 0.16, knee: -0.34, ankle: 0.18 }
        : { hip: 0.09, knee: -0.2, ankle: 0.11 }

  // Roughly half the standing height goes to the legs. The old rig put ~5
  // units below the hip against ~4.4 above it, which read as stilts.
  const thighLength = chassis === 'heavy' ? 1.24 : chassis === 'reverse' ? 1.46 : 1.4
  const shinLength = chassis === 'heavy' ? 1.32 : chassis === 'reverse' ? 1.52 : 1.46
  const thick = chassis === 'heavy' ? 1.22 : chassis === 'reverse' ? 0.9 : 1

  const hipJoint = group(hip, [side * 0.98 * width, -0.35, 0], rest.hip)
  const thigh = new PartBuilder(m)
  thigh.add(new THREE.CylinderGeometry(0.42 * thick, 0.42 * thick, 0.72, 8), 'frame', [0, 0, 0], [0, 0, Math.PI / 2])
  thigh.add(new THREE.BoxGeometry(1.18 * thick, thighLength, 1.2 * thick), 'armor', [0, -thighLength / 2 - 0.1, 0])
  thigh.add(new THREE.BoxGeometry(1.28 * thick, 0.72, 0.85), 'armorLight', [0, -0.75, -0.24])
  thigh.panelLine([1.22 * thick, 0.09, 1.24 * thick], [0, -1.28, 0])
  thigh.add(new THREE.BoxGeometry(0.3, 0.95, 0.3), 'frame', [side * 0.66 * thick, -1.0, 0.42])
  thigh.build(hipJoint)

  const knee = group(hipJoint, [0, -thighLength - 0.22, 0], rest.knee)
  const shin = new PartBuilder(m)
  shin.add(new THREE.CylinderGeometry(0.38 * thick, 0.38 * thick, 0.78, 8), 'frame', [0, 0, 0], [0, 0, Math.PI / 2])
  // Knee cap, sitting proud of the joint so the bend is legible in silhouette.
  shin.add(new THREE.BoxGeometry(0.78 * thick, 0.7, 0.42), 'armorLight', [0, 0.05, -0.6])
  shin.add(new THREE.BoxGeometry(1.02 * thick, shinLength, 1.06 * thick), 'armor', [0, -shinLength / 2 - 0.12, 0])
  shin.panelLine([1.06 * thick, 0.08, 1.1 * thick], [0, -1.15, 0])
  // Calf mass hanging off the back, the counterweight the old legs lacked.
  shin.add(new THREE.BoxGeometry(0.92 * thick, 1.15, 0.6), 'armor', [0, -1.05, 0.72], [0.14, 0, 0])
  shin.glow(new THREE.BoxGeometry(0.12, 0.68, 0.08), [0, -1.0, -0.56])
  shin.build(knee)

  const ankle = group(knee, [0, -shinLength - 0.2, 0], rest.ankle)
  const foot = new PartBuilder(m)
  foot.add(new THREE.CylinderGeometry(0.3 * thick, 0.3 * thick, 0.66, 8), 'frame', [0, 0, 0], [0, 0, Math.PI / 2])
  foot.add(new THREE.BoxGeometry(1.16 * thick, 0.42, 2.35 * thick), 'armor', [0, -0.42, -0.3])
  // Toe and heel splayed apart: a wide footprint sells the mech's weight.
  foot.add(new THREE.BoxGeometry(1.28 * thick, 0.3, 0.72), 'armorLight', [0, -0.52, -1.3])
  foot.add(new THREE.BoxGeometry(1.0 * thick, 0.34, 0.66), 'armor', [0, -0.5, 0.82])
  foot.panelLine([1.2 * thick, 0.08, 2.4 * thick], [0, -0.62, -0.3])
  foot.build(ankle)

  return { hip: hipJoint, knee, ankle, rest }
}

function buildArm(
  torso: THREE.Group,
  m: MechMaterials,
  side: number,
  chassis: MechChassis,
  width: number,
): MechArmRig {
  const rest = { shoulder: -0.06, elbow: chassis === 'heavy' ? -0.22 : -0.14 }
  const bulk = chassis === 'heavy' ? 1.26 : chassis === 'reverse' ? 0.88 : 1

  const shoulder = group(torso, [side * 1.78 * width * bulk, 2.55, 0], rest.shoulder)
  const upper = new PartBuilder(m)
  // Pauldron: the widest thing on the mech, and the main silhouette read.
  upper.add(new THREE.BoxGeometry(1.15 * bulk, 1.32, 1.72 * bulk), 'armor', [side * 0.2, 0.42, 0])
  upper.add(new THREE.BoxGeometry(1.02 * bulk, 0.44, 1.78 * bulk), 'armorLight', [side * 0.22, 1.08, 0])
  upper.panelLine([1.2 * bulk, 0.09, 1.76 * bulk], [side * 0.2, 0.78, 0])
  upper.add(new THREE.CylinderGeometry(0.36, 0.36, 0.9, 8), 'frame', [0, -0.32, 0], [0, 0, Math.PI / 2])
  upper.add(new THREE.BoxGeometry(0.82 * bulk, 1.15, 0.92 * bulk), 'armor', [0, -1.0, 0])
  upper.glow(new THREE.BoxGeometry(0.1, 0.5, 0.08), [side * 0.78 * bulk, 0.5, -0.72])
  upper.build(shoulder)

  const elbow = group(shoulder, [0, -1.68, 0], rest.elbow)
  const fore = new PartBuilder(m)
  fore.add(new THREE.CylinderGeometry(0.3, 0.3, 0.74, 8), 'frame', [0, 0, 0], [0, 0, Math.PI / 2])
  fore.add(new THREE.BoxGeometry(0.94 * bulk, 1.55, 1.02 * bulk), 'armor', [0, -0.85, -0.08])
  fore.add(new THREE.BoxGeometry(1.0 * bulk, 0.46, 1.08 * bulk), 'armorLight', [0, -0.3, -0.1])
  fore.panelLine([0.98 * bulk, 0.08, 1.06 * bulk], [0, -1.24, -0.08])
  fore.add(new THREE.BoxGeometry(0.34, 0.9, 0.34), 'frame', [side * 0.58 * bulk, -0.9, 0.44])
  fore.build(elbow)

  return { shoulder, elbow, rest }
}

function buildWeapon(elbow: THREE.Group, m: MechMaterials, chassis: MechChassis): THREE.Object3D {
  const weapon = new THREE.Group()
  weapon.position.set(0.24, -1.35, -1.0)
  elbow.add(weapon)

  const part = new PartBuilder(m)
  const length = chassis === 'heavy' ? 3.3 : 2.9
  part.add(new THREE.BoxGeometry(0.6, 0.78, length), 'frame', [0, 0, -length / 2 + 0.4])
  part.add(new THREE.BoxGeometry(0.66, 0.26, length * 0.55), 'armorLight', [0, 0.42, -length * 0.35])
  part.panelLine([0.64, 0.08, length * 0.9], [0, 0.28, -length / 2 + 0.4])
  part.add(new THREE.CylinderGeometry(0.115, 0.185, 2.45, 8), 'armorLight', [0, -0.08, -length + 0.35], [Math.PI / 2, 0, 0])
  // Muzzle brake vents.
  for (let index = 0; index < 3; index += 1) {
    part.panelLine([0.3, 0.1, 0.09], [0, 0.06, -length - 0.45 + index * 0.26])
  }
  part.add(new THREE.BoxGeometry(0.48, 0.5, 0.62), 'armor', [0, -0.5, -0.45])
  part.build(weapon)

  const muzzle = new THREE.Object3D()
  muzzle.position.set(0, -0.08, -length - 0.9)
  weapon.add(muzzle)
  return muzzle
}

function buildMissilePods(
  torso: THREE.Group,
  m: MechMaterials,
  width: number,
): THREE.Object3D[] {
  const ports: THREE.Object3D[] = []
  for (const side of [-1, 1]) {
    const pod = group(torso, [side * 1.72 * width, 2.86, 0.62])
    const part = new PartBuilder(m)
    part.add(new THREE.BoxGeometry(1.0, 1.35, 1.5), 'armor', [0, 0, 0])
    part.add(new THREE.BoxGeometry(1.06, 0.3, 1.56), 'armorLight', [0, 0.58, 0])
    part.panelLine([1.04, 0.08, 1.54], [0, 0.22, 0])
    for (let row = 0; row < 2; row += 1) {
      for (let column = 0; column < 2; column += 1) {
        const offset: Vec3 = [(column - 0.5) * 0.46, (row - 0.5) * 0.54, -0.8]
        part.add(new THREE.CylinderGeometry(0.17, 0.17, 0.14, 8), 'frame', offset, [Math.PI / 2, 0, 0])
        // The launch origin is a marker, not geometry: the pod itself is one
        // merged mesh, so ports cannot be individual Object3D children.
        const port = new THREE.Object3D()
        port.position.set(...offset)
        pod.add(port)
        ports.push(port)
      }
    }
    part.build(pod)
  }
  return ports
}

function buildThrusters(
  torso: THREE.Group,
  legs: [MechLegRig, MechLegRig],
  m: MechMaterials,
  width: number,
): THREE.Mesh[] {
  const flames: THREE.Mesh[] = []
  const nozzles = new PartBuilder(m)
  for (const x of [-1.08 * width, 1.08 * width]) {
    nozzles.add(new THREE.CylinderGeometry(0.34, 0.5, 0.72, 10), 'frame', [x, 1.58, 1.72], [Math.PI / 2, 0, 0])
  }
  nozzles.build(torso)

  for (const x of [-1.08 * width, 1.08 * width]) {
    const flame = new THREE.Mesh(new THREE.ConeGeometry(0.29, 2.6, 10, 1, true), m.hot)
    flame.position.set(x, 1.58, 2.85)
    flame.rotation.x = Math.PI / 2
    flame.scale.set(0.8, 0.35, 0.8)
    torso.add(flame)
    flames.push(flame)
  }
  for (const leg of legs) {
    const flame = new THREE.Mesh(new THREE.ConeGeometry(0.16, 1.55, 8, 1, true), m.hot)
    flame.position.set(0, -0.6, 1.0)
    flame.rotation.x = Math.PI / 2
    flame.scale.set(0.7, 0.25, 0.7)
    leg.knee.add(flame)
    flames.push(flame)
  }
  return flames
}

function buildSensor(
  head: THREE.Group,
  m: MechMaterials,
  accent: number,
  faction: 'player' | 'enemy',
): THREE.Mesh {
  const sensor = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), m.glow)
  sensor.position.set(0, 0.02, -0.55)
  head.add(sensor)
  // A short-range lamp, and a weak one. At 24+ it blew out the head plate it
  // sits inside, erasing the single most important silhouette element; the
  // visor strip is what should carry the accent, not spill from a lamp.
  const lamp = new THREE.PointLight(accent, faction === 'player' ? 7 : 10, 8, 2)
  lamp.position.set(0, 0.05, -0.8)
  head.add(lamp)
  return sensor
}

export interface MechAnimationState {
  time: number
  planarSpeed: number
  boosting: boolean
  overloaded: boolean
  /** Vertical velocity, for the landing compression. */
  verticalVelocity?: number
  airborne?: boolean
  /** 0..1 recoil impulse, decayed by the caller. */
  recoil?: number
  /** 0..1 flinch impulse with the direction it arrived from, in local space. */
  flinch?: number
  flinchYaw?: number
}

export function animateMech(rig: MechRig, state: MechAnimationState): void {
  const { time, planarSpeed, boosting, overloaded } = state
  const airborne = state.airborne ?? false
  const verticalVelocity = state.verticalVelocity ?? 0
  const recoil = state.recoil ?? 0
  const flinch = state.flinch ?? 0

  const gait = Math.min(1, planarSpeed / 9)
  const cadence = time * (5.2 + planarSpeed * 0.26)
  const stride = Math.sin(cadence) * 0.46 * gait

  // Airborne legs tuck instead of walking; on the ground the knee leads the
  // thigh by a quarter cycle, which is what makes a stride look weighted
  // rather than like a pair of scissors.
  const tuck = airborne ? 1 : 0
  const lift = Math.max(0, Math.sin(cadence)) * gait

  for (let index = 0; index < rig.legs.length; index += 1) {
    const leg = rig.legs[index]!
    const phase = index === 0 ? 1 : -1
    const swing = stride * phase
    const knee = Math.max(0, Math.sin(cadence * 1 + (phase > 0 ? 0 : Math.PI))) * 0.62 * gait

    leg.hip.rotation.x = leg.rest.hip + swing * (1 - tuck) + tuck * 0.62
    leg.knee.rotation.x = leg.rest.knee - knee * (1 - tuck) - tuck * 1.05
    leg.ankle.rotation.x = leg.rest.ankle - swing * 0.35 * (1 - tuck) + tuck * 0.42
  }

  // Landing compression: a fast downward velocity buys a deep crouch that
  // springs back, so touchdown has an impact instead of a snap to standing.
  const impact = airborne ? 0 : Math.min(1, Math.max(0, -verticalVelocity) / 14)
  const crouch = impact * 0.85
  for (const leg of rig.legs) {
    leg.hip.rotation.x += crouch * 0.45
    leg.knee.rotation.x -= crouch * 0.9
    leg.ankle.rotation.x += crouch * 0.45
  }

  rig.hip.position.y = rig.restHipHeight - lift * 0.16 - crouch * 0.9
  rig.hip.rotation.z = Math.sin(cadence) * 0.05 * gait
  rig.torso.position.y = rig.hip.position.y
  // The torso counter-rotates against the hips: opposed masses are most of
  // what separates a walking machine from a sliding model.
  rig.torso.rotation.y = -Math.sin(cadence) * 0.07 * gait
  rig.torso.rotation.z = overloaded
    ? Math.sin(time * 22) * 0.06
    : -Math.sin(cadence) * 0.03 * gait

  const lean = boosting ? -0.26 : 0
  const flinchPitch = flinch * 0.34 * Math.cos(state.flinchYaw ?? 0)
  const flinchRoll = flinch * 0.28 * Math.sin(state.flinchYaw ?? 0)
  rig.torso.rotation.x = THREE.MathUtils.lerp(
    rig.torso.rotation.x,
    lean + crouch * 0.22 + flinchPitch,
    0.16,
  )
  rig.torso.rotation.z += flinchRoll
  rig.head.rotation.x = -rig.torso.rotation.x * 0.55

  // Recoil travels shoulder → elbow so the whole arm absorbs the shot.
  const [leftArm, rightArm] = rig.arms
  rightArm.shoulder.rotation.x = rightArm.rest.shoulder - recoil * 0.55 - stride * 0.12
  rightArm.elbow.rotation.x = rightArm.rest.elbow - recoil * 0.85
  leftArm.shoulder.rotation.x = leftArm.rest.shoulder + stride * 0.3
  leftArm.elbow.rotation.x = leftArm.rest.elbow - Math.abs(stride) * 0.25

  const flameLength = boosting ? 1.5 + Math.sin(time * 50) * 0.16 : 0.42 + Math.sin(time * 28) * 0.08
  for (const flame of rig.thrusters) {
    flame.scale.y = flameLength
    flame.visible = planarSpeed > 1.2 || boosting
  }

  // The overload window is the ×1.65 opportunity, so the ring works harder
  // than an opacity blink: it swells, spins, and flickers for the whole
  // duration — one glance at the mech says "it is open right now".
  const ringMaterial = rig.shieldRing.material as THREE.MeshBasicMaterial
  ringMaterial.opacity = overloaded ? 0.66 + Math.sin(time * 18) * 0.3 : 0
  rig.shieldRing.scale.setScalar(
    overloaded ? 1.14 + Math.sin(time * 9) * 0.11 : 1 + Math.sin(time * 8) * 0.03,
  )
  rig.shieldRing.rotation.z = overloaded ? time * 2.6 : 0
}
